import { slicedToArray as _slicedToArray } from '../../_virtual/_rollupPluginBabelHelpers.js';
import _last from 'lodash/last';
import { Paragraph, Anchor, List, DataSelect, Pagination } from '@zillow/constellation';
import { dollar } from '@zillow/formatters';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import Table from '../PaymentDonutChart/Table.js';
import { handleFieldClickEvent, handleFieldChangeEvent } from './AmortizationCalculator.Clickstream.js';
import { useLastValidAmortizationValues, getZillowURL } from './AmortizationCalculator.utils.js';
import { FORM_FIELD } from './constants.js';

var AMORTIZATION_BREAKDOWN_ID = 'amortization-breakdown';
var TABLE_DISPLAY_MODE = {
  MONTH: 'month',
  YEAR: 'year'
};
var OPTIONS = {
  'Show by month': TABLE_DISPLAY_MODE.MONTH,
  'Show by year': TABLE_DISPLAY_MODE.YEAR
};
var withDollar = function withDollar(key) {
  return function (data) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, dollar(data[key]));
  };
};
var COMMON_COLUMNS = [{
  Header: 'Interest',
  accessor: 'monthlyInterest',
  Cell: withDollar('monthlyInterest')
}, {
  Header: 'Principal',
  accessor: 'monthlyPrincipal',
  Cell: withDollar('monthlyPrincipal')
}, {
  Header: 'Principal remaining',
  accessor: 'remainingBalance',
  Cell: withDollar('remainingBalance')
}];
var DATE_BASED_COLUMNS = [{
  Header: 'Date',
  accessor: 'formattedDate'
}].concat(COMMON_COLUMNS);
var MONTH_BASED_COLUMNS = [{
  Header: 'Month',
  accessor: 'month'
}].concat(COMMON_COLUMNS);
var MODES = {
  MONTH_BASED: 'month',
  DATE_BASED: 'date'
};
var getPageSizeForShowByValue = function getPageSizeForShowByValue(showBy) {
  return showBy === TABLE_DISPLAY_MODE.YEAR ? 10 : 12;
}; // Page size of 10 to represent a decade, 12 for a year

var AmortizationScheduleTable = function AmortizationScheduleTable(_ref) {
  var mode = _ref.mode;
  var form = useFormContext();
  var amortization = useLastValidAmortizationValues(form).calculations.amortization;
  var _useState = useState(TABLE_DISPLAY_MODE.MONTH),
    _useState2 = _slicedToArray(_useState, 2),
    showBy = _useState2[0],
    setShowBy = _useState2[1];
  var _useState3 = useState(getPageSizeForShowByValue(showBy)),
    _useState4 = _slicedToArray(_useState3, 2),
    pageSize = _useState4[0],
    setPageSize = _useState4[1];
  var _useState5 = useState(0),
    _useState6 = _slicedToArray(_useState5, 2),
    currentPage = _useState6[0],
    setCurrentPage = _useState6[1];
  useEffect(function () {
    setCurrentPage(0);
    setPageSize(getPageSizeForShowByValue(showBy));
  }, [showBy]);
  var filteredAmortization = useMemo(function () {
    return showBy === TABLE_DISPLAY_MODE.YEAR ? amortization.filter(function (x) {
      return x.month % 12 - 1 === 0;
    }) : amortization;
  }, [showBy, amortization]);
  var handleDataSelectChange = function handleDataSelectChange(e) {
    if (e.target.value === 'year') {
      handleFieldChangeEvent('Show by year', FORM_FIELD);
    } else if (e.target.value === 'month') {
      handleFieldChangeEvent('Show by month', FORM_FIELD);
    }
    setShowBy(e.target.value);
  };
  var amortizationPage = useMemo(function () {
    return filteredAmortization.slice(currentPage * pageSize, (currentPage + 1) * pageSize);
  }, [filteredAmortization, pageSize, currentPage]);
  var _ref2 = _last(amortization) || {},
    totalPrincipal = _ref2.totalPrincipal,
    totalInterest = _ref2.totalInterest;
  var breakdownContent = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Paragraph, {
    marginTop: "xs"
  }, "Our mortgage amortization schedule makes it easy to see how much of your mortgage payment will go toward paying interest and principal over your loan term. You can view amortization by month or year. Keep in mind, your monthly mortgage payment may also include", ' ', /*#__PURE__*/React.createElement(Anchor, {
    href: getZillowURL('mortgage-learning/what-is-piti/')
  }, "property taxes and home insurance", ' '), ' ', "- which aren't included in this amortization schedule, since the payments may fluctuate throughout your loan term."), /*#__PURE__*/React.createElement(List, {
    "aria-live": "polite",
    "aria-atomic": "true",
    marginY: "xs"
  }, /*#__PURE__*/React.createElement(List.Item, null, "Total principal payments: ", dollar(totalPrincipal)), /*#__PURE__*/React.createElement(List.Item, null, "Total interest payments: ", dollar(totalInterest))), /*#__PURE__*/React.createElement(DataSelect, {
    marginBottom: "sm",
    "aria-label": "Amortization schedule breakdown show by time length dropdown",
    defaultValue: "month",
    data: OPTIONS,
    onChange: handleDataSelectChange
  }), /*#__PURE__*/React.createElement(Table, {
    appearance: "zebra",
    columns: mode === MODES.DATE_BASED ? DATE_BASED_COLUMNS : MONTH_BASED_COLUMNS,
    rowKeyAccessor: "remainingBalance",
    items: amortizationPage,
    "aria-label": "Amortization schedule breakdown table",
    "aria-live": "polite",
    "aria-atomic": "true"
  }), /*#__PURE__*/React.createElement(Pagination, {
    marginTop: "sm",
    "aria-label": "Amortization breakdown pagination navigation by " + showBy,
    currentPageIndex: currentPage,
    onPageSelected: setCurrentPage,
    totalPages: Math.ceil(filteredAmortization.length / pageSize),
    onClick: function onClick() {
      return handleFieldClickEvent('Prev/Next', 'button');
    }
  }));
  return /*#__PURE__*/React.createElement("div", {
    id: AMORTIZATION_BREAKDOWN_ID
  }, breakdownContent);
};
AmortizationScheduleTable.propTypes = process.env.NODE_ENV !== "production" ? {
  amortization: PropTypes.arrayOf(PropTypes.shape({
    month: PropTypes.number,
    monthlyInterest: PropTypes.number,
    monthlyPrincipal: PropTypes.number,
    remainingBalance: PropTypes.number,
    formattedDate: PropTypes.string
  })),
  mode: PropTypes.oneOf(Object.values(MODES)),
  useCardStyle: PropTypes.bool
} : {};
AmortizationScheduleTable.defaultProps = {
  mode: MODES.DATE_BASED,
  useCardStyle: false
};
var AmortizationScheduleTable$1 = AmortizationScheduleTable;

export { AMORTIZATION_BREAKDOWN_ID, MODES, AmortizationScheduleTable$1 as default };
